import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EmptyState, Menu, MenuItem, MenuDivider, Popover, useToast } from '@vd/ui';
import { LayoutGrid, MoreHorizontal, Plus, Settings as SettingsIcon, Trash2, Users as UsersIcon, Clock } from 'lucide-react';
import type { Project } from '@vd/types';
import { api } from '../lib/api';
import { useAuth } from '../features/auth/AuthProvider';
import { When } from '../lib/When';
import { formatDay } from '../lib/time';
import { orderByMru, useTouchProject } from '../features/shell/projectMru';
import { CreateProjectModal } from '../features/modals/CreateProjectModal';
import { DestructiveConfirm } from '../features/modals/DestructiveConfirm';
import styles from './AllProjectsPage.module.css';
import { ProjectMark } from '../features/projects/ProjectMark';
import { openTickets } from '../features/projects/projectCounts';
import { useTabTitle, useTabWorkspaceName } from '../lib/tabTitle';

type SortKey = 'recent' | 'name' | 'created' | 'tickets';

export function AllProjectsPage() {
  // This tab's title (TOADO-905).
  const tabWorkspace = useTabWorkspaceName();
  useTabTitle({ parts: [tabWorkspace, "All projects"] });
  const { currentCompanyId } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const touchProject = useTouchProject();
  const [sort, setSort] = useState<SortKey>('recent');
  const [createOpen, setCreateOpen] = useState(false);

  const projectsQ = useQuery({
    queryKey: ['projects', currentCompanyId],
    queryFn: () => (currentCompanyId ? api.listProjects(currentCompanyId) : Promise.resolve([])),
    enabled: !!currentCompanyId,
  });

  const projects = projectsQ.data ?? [];

  // Per-card overflow menu + delete-confirm state. The Link-wrapped
  // card swallows clicks unless the trigger calls stopPropagation,
  // so the menu trigger is positioned absolutely on top.
  const [menuFor, setMenuFor] = useState<{ project: Project; anchor: DOMRect } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteProject(deleteTarget.id);
      await qc.invalidateQueries({ queryKey: ['projects', currentCompanyId] });
      toast.push({ variant: 'success', title: 'Project deleted', description: `${deleteTarget.name} is hidden. Hard-purges in 30 days.` });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Delete failed', description: (e as Error).message });
    } finally {
      setDeleteTarget(null);
    }
  };

  const sorted = useMemo(() => {
    const arr = [...projects];
    if (sort === 'name') {
      arr.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sort === 'created') {
      arr.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
    } else if (sort === 'tickets') {
      arr.sort((a, b) => openTickets(b) - openTickets(a));
    } else {
      // "recent" — sort by server-tracked lastOpenedAt desc, falling back
      // to lastActivityAt so projects you've never personally opened still
      // order sensibly.
      return orderByMru(arr);
    }
    return arr;
  }, [projects, sort]);

  const handleCreate = async (data: { name: string; color: string; urlPattern?: string }) => {
    if (!currentCompanyId) return;
    try {
      const project = await api.createProject(currentCompanyId, {
        name: data.name,
        color: data.color,
        urlPattern: data.urlPattern,
      });
      await qc.invalidateQueries({ queryKey: ['projects', currentCompanyId] });
      toast.push({ variant: 'success', title: 'Project created', description: project.name });
      touchProject(project.id);
      navigate(`/projects/${project.id}/board`);
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Could not create project', description: (e as Error).message });
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headingRow}>
          <div className={styles.iconSlot}><LayoutGrid size={18} /></div>
          <div className={styles.textCol}>
            <h1 className={styles.title}>All projects</h1>
            <p className={styles.subtitle}>Every project in this workspace. Click one to open its board.</p>
          </div>
          <span className={styles.countChip}>{projects.length}</span>
          <button
            type="button"
            className={styles.newBtn}
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={14} /> New project
          </button>
        </div>

        <div className={styles.controls}>
          <label className={styles.sortLabel} htmlFor="all-projects-sort">Sort by</label>
          <select
            id="all-projects-sort"
            className={styles.sortSelect}
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
          >
            <option value="recent">Most recent</option>
            <option value="name">Name (A to Z)</option>
            <option value="created">Date created</option>
            <option value="tickets">Open tickets</option>
          </select>
        </div>
      </header>

      <div className={styles.scroll}>
        {projects.length === 0 && !projectsQ.isLoading ? (
          <div className={styles.emptyWrap}>
            <EmptyState
              title="No projects yet"
              description="Create a project to start capturing tickets from the extension or the widget."
              action={
                <button type="button" className={styles.newBtn} onClick={() => setCreateOpen(true)}>
                  <Plus size={14} /> New project
                </button>
              }
            />
          </div>
        ) : (
          <div className={styles.grid}>
            {sorted.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                onClick={() => touchProject(p.id)}
                onMenu={(anchor) => setMenuFor({ project: p, anchor })}
              />
            ))}
          </div>
        )}
      </div>

      <CreateProjectModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        existingColors={(projectsQ.data ?? []).map((p) => p.color)}
      />

      {/* Per-card overflow menu. The Popover is rendered once at page
          level and re-anchored on each open; Settings opens the
          project's settings page; Delete uses the inline
          DestructiveConfirm so the user doesn't have to navigate
          away to confirm. */}
      <Popover
        open={!!menuFor}
        onClose={() => setMenuFor(null)}
        anchor={menuFor?.anchor ?? null}
        placement="bottom-end"
      >
        <Menu>
          <MenuItem
            leftIcon={<SettingsIcon size={14} />}
            onClick={() => {
              const p = menuFor?.project;
              setMenuFor(null);
              if (p) navigate(`/projects/${p.id}/settings`);
            }}
          >
            Project settings
          </MenuItem>
          <MenuDivider />
          <MenuItem
            leftIcon={<Trash2 size={14} />}
            destructive
            onClick={() => {
              const p = menuFor?.project;
              setMenuFor(null);
              if (p) setDeleteTarget(p);
            }}
          >
            Delete project…
          </MenuItem>
        </Menu>
      </Popover>

      <DestructiveConfirm
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={`Delete ${deleteTarget?.name ?? 'project'}?`}
        description={`This soft-deletes ${deleteTarget?.name ?? 'the project'} and all its tickets. They're hidden immediately and hard-purged in 30 days. This can't be undone after that window.`}
        typeToConfirm="DELETE"
        confirmLabel="Delete project"
      />
    </div>
  );
}

interface ProjectCardProps {
  project: Project;
  onClick: () => void;
  onMenu: (anchor: DOMRect) => void;
}

function ProjectCard({ project, onClick, onMenu }: ProjectCardProps) {
  const lastActivity = project.lastActivityAt ?? project.createdAt;
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  return (
    <Link
      to={`/projects/${project.id}/board`}
      onClick={onClick}
      className={styles.card}
      data-testid="all-projects-card"
      data-project-id={project.id}
    >
      {/* Overflow trigger sits absolutely in the top-right of the
          card. Clicking it opens the menu and stops propagation so
          the parent Link doesn't navigate to the board. */}
      <button
        ref={moreBtnRef}
        type="button"
        aria-label={`${project.name} actions`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (moreBtnRef.current) {
            onMenu(moreBtnRef.current.getBoundingClientRect());
          }
        }}
        style={{
          position: 'absolute',
          top: 8, right: 8,
          width: 24, height: 24,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'transparent',
          border: 'none', borderRadius: 4,
          color: 'var(--text-tertiary)',
          cursor: 'pointer',
          zIndex: 1,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-muted)'; e.currentTarget.style.color = 'var(--text)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
      >
        <MoreHorizontal size={14} />
      </button>

      <div className={styles.cardHead}>
        <ProjectMark project={project} size={22} />
        <span className={styles.cardName}>{project.name}</span>
      </div>
      {project.description && <div className={styles.cardDesc}>{project.description}</div>}
      <div className={styles.cardStats}>
        {/* Open, then done and archived (TOADO-943): what the sidebar's number counts, and the rest. */}
        {project.openCount === undefined
          ? <Stat label="Tickets" value={String(project.ticketCount)} />
          : <>
              <Stat label="Open" value={String(project.openCount)} />
              <Stat label="Done" value={String(project.doneCount ?? 0)} />
              <Stat label="Archived" value={String(project.archivedCount ?? 0)} />
            </>}
        <Stat label="People" value={String(project.memberCount ?? 0)} icon={<UsersIcon size={12} />} />
      </div>
      <div className={styles.cardFoot}>
        <span className={styles.footRow}>
          <Clock size={11} />
          <span className={styles.footLabel}>Last edited</span>
          <span className={styles.footValue}>{lastActivity ? <When at={lastActivity} /> : '-'}</span>
        </span>
        <span className={styles.footRow}>
          <span className={styles.footLabel}>Created</span>
          <span className={styles.footValue}>{project.createdAt ? formatDate(project.createdAt) : '-'}</span>
        </span>
      </div>
      <span className={styles.cardOpen}>Open board →</span>
    </Link>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className={styles.stat}>
      <div className={styles.statHead}>
        {icon}
        <span>{label}</span>
      </div>
      <div className={styles.statValue}>{value}</div>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return formatDay(d);
}
